import { useState, useMemo, useEffect, useCallback } from "react";
import {
  formatCountWithArabicNumbers,
  formatPercentageWithArabicNumbers,
} from "@/utils/arabicNumbers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Calendar as CalendarIcon,
  User,
  FileText,
  FileDown,
  Loader2,
} from "lucide-react";
import { ArabicCalendar } from "@/components/ui/arabic-calendar";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { generateStudentAttendancePDF } from "@/utils/studentAttendancePdfGenerator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AttendanceReportFilters } from "../filters/AttendanceReportFilters";
import { supabase } from "@/integrations/supabase/client";
import { dateSplitter } from "@/utils/formatting/deteSplitter";

// دالة للحصول على اسم اليوم بالعربية
function getArabicDayName(date: Date): string {
  const days = [
    "الأحد",
    "الإثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];
  return days[date.getDay()];
}

// دالة تنسيق التاريخ الهجري
const formatHijriDate = (date: Date): string => {
  try {
    // استخدام Intl.DateTimeFormat للحصول على تاريخ هجري دقيق
    const hijriFormatter = new Intl.DateTimeFormat(
      "ar-SA-u-ca-islamic-umalqura",
      {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    );

    const parts = hijriFormatter.formatToParts(date);
    const day = parts.find((part) => part.type === "day")?.value || "01";
    const month = parts.find((part) => part.type === "month")?.value || "01";
    const year = parts.find((part) => part.type === "year")?.value || "1446";

    // تحويل الأرقام إلى أرقام هندية
    const convertToArabicNumbers = (num: string): string => {
      const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
      return num.replace(/\d/g, (digit) => arabicNumerals[parseInt(digit)]);
    };

    // ترتيب: سنة/شهر/يوم (من اليمين لليسار)
    return `${convertToArabicNumbers(year)}/${convertToArabicNumbers(
      month
    )}/${convertToArabicNumbers(day)} هـ`;
  } catch (error) {
    console.error("خطأ في تحويل التاريخ الهجري:", error);
    // بديل: استخدام التاريخ الميلادي مع أرقام هندية
    const fallback = date
      .toLocaleDateString("ar-SA")
      .replace(/\d/g, (digit) => {
        const arabicNumerals = [
          "٠",
          "١",
          "٢",
          "٣",
          "٤",
          "٥",
          "٦",
          "٧",
          "٨",
          "٩",
        ];
        return arabicNumerals[parseInt(digit)];
      });
    return fallback;
  }
};

interface StudentReportTabProps {
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  selectedStage: string;
  handleStageChange: (value: string) => void;
  selectedGrade: string;
  handleGradeChange: (value: string) => void;
  selectedSection: string;
  setSelectedSection: (value: string) => void;
  students: any[];
  lateRecords?: any[];
  validEnabledStages: any[];
  filteredAvailableGrades: string[];
  availableSectionsForGrade: string[];
  safeGetStageDisplayName: (stage: any) => string;
  // selectedStudent: string;
  // setSelectedStudent: (value: string) => void;
}

export const StudentReportTab = ({
  students,
  // lateRecords,
  validEnabledStages,
  safeGetStageDisplayName,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedStage,
  handleStageChange,
  selectedGrade,
  handleGradeChange,
  selectedSection,
  setSelectedSection,
  // selectedStudent,
  // setSelectedStudent,
  filteredAvailableGrades,
  availableSectionsForGrade,
}: StudentReportTabProps) => {
  // const [selectedStage, setSelectedStage] = useState<string>("");
  // const [selectedGrade, setSelectedGrade] = useState<string>("");
  // const [selectedSection, setSelectedSection] = useState<string>("");
  // const [selectedStudent, setSelectedStudent] = useState<string>("");
  // const [startDate, setStartDate] = useState<Date>();
  // const [endDate, setEndDate] = useState<Date>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<string>("");
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);

  // console.log("attendanceRecords: ", attendanceRecords);
  // Get available grades for selected stage
  // const availableGrades = useMemo(() => {
  //   if (validEnabledStages.length > 1 && !selectedStage) return [];

  //   // If there's only one stage, use it automatically
  //   const stageToUse =
  //     validEnabledStages.length === 1
  //       ? validEnabledStages[0]
  //       : validEnabledStages.find((s) => s?.id === selectedStage);
  //   return stageToUse?.grades?.map((g: any) => g?.name).filter(Boolean) || [];
  // }, [selectedStage, validEnabledStages]);

  // // Get available sections for selected grade
  // const availableSections = useMemo(() => {
  //   if (!selectedGrade) return [];

  //   // If there's only one stage, use it automatically
  //   const stageToUse =
  //     validEnabledStages.length === 1
  //       ? validEnabledStages[0]
  //       : validEnabledStages.find((s) => s?.id === selectedStage);
  //   if (!stageToUse) return [];

  //   const grade = stageToUse.grades?.find(
  //     (g: any) => g?.name === selectedGrade
  //   );
  //   return grade?.sections?.filter(Boolean) || [];
  // }, [selectedGrade, selectedStage, validEnabledStages]);

  // // Get available students for selected filters
  const availableStudents = useMemo(() => {
    return students.filter((student) => {
      // If there are multiple stages and one is selected, filter by it
      if (validEnabledStages.length > 1 && selectedStage) {
        const stage = validEnabledStages.find((s) => s?.id === selectedStage);
        if (stage?.grades) {
          const stageGrades = stage.grades.map((g: any) => g?.name);
          if (!stageGrades.includes(student.grade)) return false;
        }
      }
      // If there's only one stage, check if student's grade exists in that stage
      else if (validEnabledStages.length === 1) {
        const stage = validEnabledStages[0];
        if (stage?.grades) {
          const stageGrades = stage.grades.map((g: any) => g?.name);
          if (!stageGrades.includes(student.grade)) return false;
        }
      }

      if (selectedGrade && student.grade !== selectedGrade) return false;
      if (selectedSection && student.section !== selectedSection) return false;
      return true;
    });
  }, [
    students,
    selectedStage,
    selectedGrade,
    selectedSection,
    validEnabledStages,
  ]);

  // Get student data for the report
  const studentData = useMemo(() => {
    if (!selectedStudent) return null;

    const student = students.find((s) => s.id === selectedStudent);
    if (!student) return null;

    // Filter attendance records for the student and date range
    const studentAttendance = attendanceRecords.filter((record) => {
      if (record.student_id !== selectedStudent) return false;
      if (startDate || endDate) {
        const recordDate = new Date(record.date);
        if (startDate && recordDate < startDate) return false;
        if (endDate && recordDate > endDate) return false;
      }
      return true;
    });

    // console.log("studentAttendance attendance: ", studentAttendance);
    // Filter late records for the student and date range
    // const studentLate = lateRecords.filter((record) => {
    //   if (record.studentId !== selectedStudent) return false;
    //   if (startDate || endDate) {
    //     const recordDate = new Date(record.date);
    //     if (startDate && recordDate < startDate) return false;
    //     if (endDate && recordDate > endDate) return false;
    //   }
    //   return true;
    // });

    // Calculate statistics
    const totalDays =
      startDate && endDate
        ? Math.ceil(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
          ) + 1
        : studentAttendance.length;
    const absentDays = studentAttendance.filter(
      (r) => r.status === "absent" || r.status === "absent_with_reason"
    );
    const absentDaysWithReason = studentAttendance.filter(
      (r) => r.status === "absent_with_reason"
    );
    const presentDays = studentAttendance.filter((r) => r.status === "present");
    // const lateDays = studentLate;

    const absentCount = absentDays.length;
    const presentCount = presentDays.length;
    // const lateCount = lateDays.length;
    const absentPercentage =
      totalDays > 0 ? Math.round((absentCount / totalDays) * 100) : 0;
    // const latePercentage =
    //   totalDays > 0 ? Math.round((lateCount / totalDays) * 100) : 0;

    return {
      student,
      totalDays,
      absentCount,
      presentCount,
      // lateCount,
      absentPercentage,
      // latePercentage,
      absentDays,
      absentDaysWithReason,
      // lateDays,
    };
  }, [
    selectedStudent,
    startDate,
    endDate,
    students,
    attendanceRecords,
    // lateRecords,
  ]);

  const fetchLates = useCallback(async () => {
    if (!selectedStudent) {
      setAttendanceRecords([]);
      return;
    }

    let query = supabase
      .from("attendance_records")
      .select("*")
      .eq("student_id", selectedStudent);

    if (startDate) {
      query = query.gte("date", dateSplitter(startDate));
    }
    if (endDate) {
      query = query.lte("date", dateSplitter(endDate));
    }

    const { data, error } = await query;
    if (error) {
      // handle error (toast, etc.)
      setAttendanceRecords([]);
    } else {
      setAttendanceRecords(data || []);
    }
    // setLoadingProfiles(false);
  }, [endDate, selectedStudent, startDate]);

  useEffect(() => {
    if (selectedStudent) fetchLates();
  }, [fetchLates, selectedStudent]);

  // const handleStageChange = (value: string) => {
  //   setSelectedStage(value);
  //   setSelectedGrade("");
  //   setSelectedSection("");
  //   setSelectedStudent("");
  // };

  // const handleGradeChange = (value: string) => {
  //   setSelectedGrade(value);
  //   setSelectedSection("");
  //   setSelectedStudent("");
  // };

  // const handleSectionChange = (value: string) => {
  //   setSelectedSection(value);
  //   setSelectedStudent("");
  // };

  // Handle PDF download for student attendance report
  const handleDownloadStudentPDF = async () => {
    if (!selectedStudent || !startDate) {
      toast({
        title: "بيانات مطلوبة",
        description: "يجب اختيار الطالب وتحديد تاريخ البداية لإنشاء التقرير",
        variant: "destructive",
      });
      return;
    }

    if (!studentData) {
      toast({
        title: "لا توجد بيانات",
        description: "لا توجد بيانات للطالب المحدد في الفترة المختارة",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);

    try {
      toast({
        title: "بدء التحميل",
        description: "جاري إنشاء تقرير حضور الطالب PDF...",
      });

      // Create student report data
      const reportData = {
        student: studentData.student,
        period: endDate
          ? `من ${formatHijriDate(startDate)} إلى ${formatHijriDate(endDate)}`
          : `من ${formatHijriDate(startDate)}`,
        totalDays: studentData.totalDays,
        presentCount: studentData.presentCount,
        absentCount: studentData.absentCount,
        absentWithReasonCount: studentData.absentDaysWithReason.length,
        absentPercentage: studentData.absentPercentage,
        absentDays: studentData.absentDays.map((record) => ({
          date: format(new Date(record.date), "yyyy/MM/dd"),
          day: getArabicDayName(new Date(record.date)),
          hijriDate: formatHijriDate(new Date(record.date)),
          notes: record.notes || "",
          status: record.status,
          absent_reason: record.absent_reason,
        })),
      };

      // Generate PDF using the professional generator
      await generateStudentAttendancePDF(reportData);

      toast({
        title: "تم التنزيل بنجاح",
        description: "تم تنزيل تقرير حضور الطالب بصيغة PDF",
        duration: 3000,
      });
    } catch (error) {
      console.error("خطأ في تنزيل تقرير الطالب:", error);
      toast({
        title: "خطأ في التنزيل",
        description: "حدث خطأ أثناء تنزيل تقرير الطالب",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Download Button */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={handleDownloadStudentPDF}
          className="bg-primary-blue hover:bg-primary-blue/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          size="sm"
          disabled={!studentData || isDownloading || !startDate}
        >
          {isDownloading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin text-orange-600" />
              <span className="text-orange-600">جاري التحميل...</span>
            </>
          ) : (
            <>
              <FileDown className="h-4 w-4 mr-2" />
              تنزيل تقرير PDF
            </>
          )}
        </Button>
      </div>
      {/* Filters */}
      <AttendanceReportFilters
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedStage={selectedStage}
        handleStageChange={handleStageChange}
        selectedGrade={selectedGrade}
        handleGradeChange={handleGradeChange}
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        validEnabledStages={validEnabledStages}
        safeGetStageDisplayName={safeGetStageDisplayName}
        filteredAvailableGrades={filteredAvailableGrades}
        availableSectionsForGrade={availableSectionsForGrade}
        selectedStudent={selectedStudent}
        setSelectedStudent={setSelectedStudent}
        enableStudentSelect
        filteredStudents={availableStudents}
      />

      {selectedStudent && !startDate && (
        <Card>
          <CardContent className="text-center py-8">
            <div className="text-muted-foreground">
              الخطوة التالية: حدد تاريخ البداية لعرض التقرير
            </div>
          </CardContent>
        </Card>
      )}

      {/* Student Report */}
      {studentData && startDate && (
        <div className="space-y-6">
          {/* Student Info */}
          <Card>
            <div className="bg-secondary text-white rounded-t-lg p-4">
              <div className="flex items-center justify-center gap-2">
                <User className="h-5 w-5 text-white" />
                <h3 className="text-lg font-semibold text-white">
                  تقرير الطالب: {studentData.student.name}
                </h3>
              </div>
            </div>
          </Card>

          {/* Attendance Report */}
          <Card>
            <div className="bg-secondary text-white rounded-t-lg p-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-white" />
                <h3 className="text-lg font-semibold text-white">
                  تقرير الحضور والغياب
                </h3>
              </div>
            </div>
            <CardContent className="pt-6">
              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-transparent via-brand-blue/10 to-transparent rounded-xl border-l-4 border-brand-blue border border-brand-blue/20 shadow-sm hover:shadow-md transition-shadow backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-blue/10 rounded-full flex items-center justify-center">
                        <CalendarIcon className="h-4 w-4 text-brand-blue" />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-brand-blue">
                      أيام الفترة
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-brand-blue">
                      {formatCountWithArabicNumbers(studentData.totalDays)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-transparent via-brand-green/10 to-transparent rounded-xl border-l-4 border-brand-green border border-brand-green/20 shadow-sm hover:shadow-md transition-shadow backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-green/10 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-brand-green" />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-brand-green">
                      أيام الحضور
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-brand-green">
                      {formatCountWithArabicNumbers(studentData.presentCount)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-transparent via-brand-red/10 to-transparent rounded-xl border-l-4 border-brand-red border border-brand-red/20 shadow-sm hover:shadow-md transition-shadow backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-red/10 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-brand-red" />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-brand-red">
                      أيام الغياب
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-brand-red">
                      {formatCountWithArabicNumbers(studentData.absentCount)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-transparent via-brand-orange/10 to-transparent rounded-xl border-l-4 border-brand-orange border border-brand-orange/20 shadow-sm hover:shadow-md transition-shadow backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-orange/10 rounded-full flex items-center justify-center">
                        <FileDown className="h-4 w-4 text-brand-orange" />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-brand-orange">
                      نسبة الغياب
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-brand-orange">
                      {formatPercentageWithArabicNumbers(
                        studentData.absentPercentage
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Absence Table */}
              {studentData.absentDays.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">تفاصيل أيام الغياب</h4>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-teal-500 text-white">
                          <th className="px-6 py-4 text-right font-medium">
                            م
                          </th>
                          <th className="px-6 py-4 text-right font-medium">
                            اليوم
                          </th>
                          <th className="px-6 py-4 text-right font-medium">
                            التاريخ
                          </th>
                          <th className="px-6 py-4 text-right font-medium">
                            الحالة
                          </th>
                          <th className="px-6 py-4 text-right font-medium">
                            سبب الغياب
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {studentData.absentDays.map((record, index) => (
                          <tr
                            key={record.id}
                            className="border-b  hover:bg-gray-50/50"
                          >
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 font-medium text-sm">
                                {index + 1}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {getArabicDayName(new Date(record.date))}
                            </td>
                            <td className="px-6 py-4 font-medium">
                              {formatHijriDate(new Date(record.date))}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-1 items-center">
                                <span
                                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                                    record.status === "absent_with_reason"
                                      ? "bg-brand-blue/10 text-brand-blue border-brand-blue/20"
                                      : "bg-red-100 text-red-700 border-red-200 border"
                                  }`}
                                >
                                  {record.status === "absent_with_reason"
                                    ? "غائب بعذر"
                                    : "غائب"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span>
                                {record.status === "absent_with_reason"
                                  ? record?.absent_reason
                                  : "لا يوجد"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Data Message */}
      {!studentData && selectedStudent && (
        <Card>
          <CardContent className="text-center py-8">
            <div className="text-muted-foreground">
              لا توجد بيانات حضور للطالب في الفترة المحددة
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
